import React, { useState, useContext, useEffect, useMemo } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import { FormText } from "reactstrap";
import * as Yup from "yup";
import axios from "axios";
import Select from "react-select";
import { Redirect } from "react-router-dom";
import AsyncDropdownNormal from "../../components/asyncDropdownNormal";
import AsyncDropdownSpotify from "../../components/asyncDropdownSpotify";
import AsyncDropdownDeezer from "../../components/asyncDropdownDeezer";
import AsyncDropdownAmazon from "../../components/asyncDropdownAmazon";
import {
  Input,
  Card,
  Row,
  CardBody,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  FormGroup,
  Label,
  Button,
  Alert,
  CardTitle
} from "reactstrap";
import { getCookie } from "../../jwt/_helpers/cookie";
import { StateContext } from "../../utils/context";
import AsyncSelect from "react-select/async";
import ProfileImg from "../../assets/images/users/1.jpg";
import { getUser } from "../../utils/getUser";
import { Trans, useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const ArtistsAdd = () => {
  const token = getCookie("token");
  const options = {
    method: "GET",
    mode: 'cors',
    headers: {
      Authorization: `Bearer ${token}`,
      "x-api-key": X_API_KEY,
      "Content-Type": "application/json"
    }
  };
  const {currentUser, setCurrentUser} = useContext(StateContext);
  const { t } = useTranslation();
  const [success, setSuccess] = useState(false);
  const [errorDupl, setErrorDupl] = useState(false);
  const [errorNameMismatch, setErrorNameMismatch] = useState(false);
  const [errorProfileUnmatched, setErrorProfileUnmatched] = useState(false);
  const [errorBlacklisted, setErrorBlacklisted] = useState(false);
  
  const [error, setError] = useState(false);
  const [redirect, setRedirect] = useState(false);

  const [parentUserOnly, setParentUserOnly] = useState('&parent_user_only=true');
  const [subUser, setSubUser] = useState('');
  const [subUserId, setSubUserId] = useState(null);
  const [subUserEndpoint, setSubUserEndpoint] = useState('');

  const [profileMissingModalVisible, setProfileMissingModalVisible] = useState(false);
  const [newArtistModalVisible, setNewArtistModalVisible] = useState(false);
  const [isNewArtist, setIsNewArtist] = useState(null);
  const [hasSpotifyProfile, setHasSpotifyProfile] = useState(true);
  const [hasDeezerProfile, setHasDeezerProfile] = useState(true);
  const [hasAmazonProfile, setHasAmazonProfile] = useState(true);
  const [hasAppleProfile, setHasAppleProfile] = useState(true);
  const [spotifyArtist, setSpotifyArtist] = useState(null);
  const [deezerArtist, setDeezerArtist] = useState(null);
  const [amazonArtist, setAmazonArtist] = useState(null);
  const [defaultQuery, setDefaultQuery] = useState("");
  const [missingStoreName, setMissingStoreName] = useState("");

  const [imageBig, setImageBig] = useState("");
  const [imageSmall, setImageSmall] = useState("");

  const [namePreference, setNamePreference] = useState("spotify");

  const enabledProfilesCount = [hasSpotifyProfile, hasDeezerProfile, hasAmazonProfile].filter(Boolean).length;
  const selectedArtistsCount = [spotifyArtist, deezerArtist, amazonArtist].filter(Boolean).length;

  const isNewArtistOptions = [
    { label: t("artists.add.form.option.yes"), value: "yes" },
    { label: t("artists.add.form.option.no"), value: "no" }
  ];

  const CustomOption = useMemo(() => {
    return React.memo(({ innerProps, data, isFocused }) => (
      <div {...innerProps} style={{ 
          padding: '3px',
          backgroundColor: isFocused ? '#f5f5f5' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer'
        }}>
        <img
          src={data.image_small || ProfileImg}
          alt={data.label}
          style={{
            padding: "5px",
            height: '65px',
            width: '65px',
            borderRadius: '30%',
            marginRight: "7px",
          }}
        />
        <b>{data.label}</b>
      </div>
    ));
  }, []);

  useEffect(() => {
    getUser(token, currentUser, setCurrentUser);
  }, [success, error]);

  useEffect(() => {
    if(missingStoreName) {
      setProfileMissingModalVisible(true);
    }
  }, [missingStoreName])

  useEffect(() => {
    if(!isNewArtist) {
      let amazonArtistName = amazonArtist ? amazonArtist.name.toLowerCase() : null;
      let deezerArtistName = deezerArtist ? deezerArtist.name.toLowerCase() : null;
      let spotifyArtistName = spotifyArtist ? spotifyArtist.name.toLowerCase() : null;

      const artistNames = [amazonArtistName, deezerArtistName, spotifyArtistName].filter(name => name !== null);
      const allNamesMatch = artistNames.every(name => name === artistNames[0]);

      if (artistNames.length > 0) {
        setErrorNameMismatch(!allNamesMatch);
      }

      if (
        (hasSpotifyProfile && !spotifyArtist) ||
        (hasDeezerProfile && !deezerArtist) ||
        (hasAmazonProfile && !amazonArtist)
      ) {
        setErrorProfileUnmatched(true);
      } else {
        setErrorProfileUnmatched(false);
      }
    }
  }, [amazonArtist, deezerArtist, spotifyArtist, hasSpotifyProfile, hasDeezerProfile, hasAmazonProfile]);

  const validationSchema = Yup.object().shape({
    name: Yup.string()
    .required(t("artists.add.form.validation.name_required"))
    .test('is-dj-correct', t("artists.add.form.validation.name_invalid"), function(value) {
      if (isNewArtist && value && value.toLowerCase().includes('dj') && !value.includes('DJ')) {
        return false;
      }
      return true;
    }),
    email: Yup.string()
      .required(t("artists.add.form.validation.email_required"))
      .email(t("artists.add.form.validation.email_invalid")),
    spotifyId: Yup.string()
      .matches(
        /^[0-9A-Za-z_-]{22}$/,
        t("artists.add.form.validation.spotify_id_invalid")
      ),
    appleId: (!isNewArtist && hasAppleProfile)
      ? Yup.string()
          .required(t("artists.add.form.validation.apple_id_required"))
          .matches(
            /^[0-9]*$/,
            t("artists.add.form.validation.apple_id_invalid")
          )
      : Yup.string().notRequired(),
  });
    
  return (
    <div>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <CardTitle tag="h4">{t("artists.add.title")}</CardTitle>
              <Formik
                initialValues={{
                  name: "",
                  email: "",
                  spotifyId: "",
                  deezerId: "",
                  amazonId: "",
                  appleId: "",
                }}
                validationSchema={validationSchema}
                onSubmit={(
                  { name, email, amazonId, appleId, deezerId, spotifyId },
                  { setStatus, setSubmitting }
                ) => {
                  setStatus();
                  setErrorDupl(false);
                  setErrorNameMismatch(false);
                  setErrorBlacklisted(false);

                  const options = {
                    method: "POST",
                    mode: 'cors',
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "x-api-key": X_API_KEY,
                      "Content-Type": "application/json"
                    }
                  };

                  axios.get(`${API_URL}artists/${subUserEndpoint}?search=${name}${parentUserOnly}`, options).then(res => {
                    if (res.status === 200){
                      const list = res.data.results.length > 0 ? res.data.results : [];

                      const getOne = list.filter(i => i.name === name);

                      if (getOne.length > 0) {
                        setErrorDupl(true)
                      } else {
                        axios
                        .post(
                          `${API_URL}artists/`,
                          {
                            "name": name,
                            "email": email,
                            "spotify_identifier": spotifyId,
                            "apple_identifier": appleId,
                            "deezer_identifier": deezerId,
                            "amazon_music_identifier": amazonId,
                            "image_big": imageBig,
                            "image_small": imageSmall,
                            "followers": spotifyArtist ? spotifyArtist.followers : 0,
                            "popularity": spotifyArtist ? spotifyArtist.popularity : 0,
                            "sub_user_id": subUserId ?  subUserId: null,
                          },
                          options
                        )
                        .then(res => {
                          setSuccess(true);
                          setTimeout(() => setRedirect(true), 1000);
                        })
                        .catch(err => {
                          if (err.response && err.response.data) {
                            const errorData = err.response.data;
                            if (errorData.blacklisted && Array.isArray(errorData.blacklisted) && errorData.blacklisted.length > 0) {
                              setErrorBlacklisted(true);
                            } else {
                              setError(true);
                            }
                          } else {
                            setError(true);
                          }
                        });
                      }
                    }
                  })
                }}
                render={({
                  errors,
                  status,
                  touched,
                  isSubmitting,
                  setFieldValue,
                }) => {
                  return (
                    <Form className="mt-3" id="addArtistForm">

                      <FormGroup row>
                        <Label sm={2}>{t("artists.add.form.label.is_new_artist")}</Label>
                        <Col sm={10}>
                          <Select
                            id="isNewArtist"
                            name="isNewArtist"
                            value={isNewArtist? { label: t("artists.add.form.option.yes"), value: "yes" }: { label: t("artists.add.form.option.no"), value: "no" }}
                            components={{ IndicatorSeparator: () => null }}
                            options={isNewArtistOptions}
                            onChange={option => {
                              if(option.value === "yes") {
                                if(!isNewArtist) {
                                  setNewArtistModalVisible(true)
                                }
                              } else {
                                if(isNewArtist) {
                                  setIsNewArtist(false)
                                }
                              }
                            }}
                          />
                          <FormText color="muted">
                            {t("artists.add.form.help_text.is_new_artist")}
                          </FormText>
                        </Col>
                      </FormGroup>
                      
                        { (!isNewArtist && hasSpotifyProfile) &&
                        <>
                          <FormGroup className="select-search-wrap" row>
                            <Label for="Spotify Profile" sm={2}>
                              {t("artists.add.form.label.spotify_profile")}
                            </Label>
                            <Col sm={8}>
                              {spotifyArtist?
                                <AsyncSelect
                                  defaultOptions={[spotifyArtist]}
                                  value={spotifyArtist}
                                  onChange={()=> {}}
                                  loadingMessage={() => t("general.asyncDropdown.loading")}
                                  noOptionsMessage={() => t("general.asyncDropdown.noResults")} 
                                  components={{
                                    Option: CustomOption,
                                  }}
                                  styles={{
                                    menu: (styles) => ({ ...styles, zIndex: 10 }),
                                  }}
                                />:
                                <>
                                  <AsyncDropdownSpotify
                                    fetchOptions={options}
                                    labelField="name"
                                    onChange={e => {
                                      let artistId = e.value;
                                      let artistObj = e;
                                      artistObj.name = e.label;
                                      setSpotifyArtist(artistObj);
                                      setFieldValue("spotifyId", artistId);
                                      setImageBig(artistObj.image_big);
                                      setImageSmall(artistObj.image_small);
                                      if(defaultQuery !== artistObj.name) {
                                        setDefaultQuery(artistObj.name);
                                      }
                                      if(!deezerArtist && !amazonArtist) {
                                        setNamePreference("spotify");
                                        setFieldValue("name", artistObj.name);
                                      }
                                    }}
                                    defaultQuery={defaultQuery}
                                    isDisabled={spotifyArtist? true: false}
                                    placeholder={t("artists.add.form.placeholder.spotify_profile")}
                                  />
                                  <FormText color="muted">
                                    {t("artists.add.form.help_text.spotify_profile")}
                                  </FormText>
                                </>
                              }
                            </Col>
                            <Col sm={2}>
                              {
                                spotifyArtist? 
                                <Button
                                  className="btn btn-outline-info"
                                  style={{width: '100%'}}
                                  onClick={() => {
                                    setSpotifyArtist(null);
                                    setErrorNameMismatch(false);
                                    setErrorDupl(false);
                                    setErrorBlacklisted(false);
                                    setFieldValue("spotifyId", "");
                                    if(!deezerArtist && !amazonArtist) {
                                      setFieldValue("name", "");
                                      setImageBig("");
                                      setImageSmall("");
                                    } else if(namePreference === "spotify") {
                                      if(deezerArtist) {
                                        setNamePreference("deezer");
                                        setFieldValue("name", deezerArtist.name);
                                        setImageBig(deezerArtist.image_big);
                                        setImageSmall(deezerArtist.image_small);
                                      } else if (amazonArtist) {
                                        setNamePreference("amazon");
                                        setFieldValue("name", amazonArtist.name);
                                        setImageBig(amazonArtist.image_big);
                                        setImageSmall(amazonArtist.image_small);
                                      }
                                    }
                                  }}
                                >
                                  {t("artists.add.form.button.reset")}
                                </Button>:
                                <Button
                                  className="btn btn-outline-danger"
                                  style={{width: '100%'}}
                                  onClick={() => {
                                    if(enabledProfilesCount>=2) {
                                      setMissingStoreName("Spotify");
                                    }
                                  }}
                                >
                                  {t("artists.add.form.button.not_found")}
                                </Button>
                              }
                            </Col>
                          </FormGroup>

                          {
                            spotifyArtist &&
                            <FormGroup row>
                              <Label for="spotifyId" sm={2}>
                                {t("artists.add.form.label.spotify_id")}
                              </Label>
                              <Col>
                                <Field
                                  name="spotifyId"
                                  type="text"
                                  disabled={true}
                                  className="form-control"
                                />
                              </Col>
                            </FormGroup>
                          }
                          {
                            (spotifyArtist && spotifyArtist.image_big) && 
                            <FormGroup row>
                              <Label sm={2}>{t("artists.add.form.label.spotify_image")}</Label>
                              <Col xs={2}>
                                <img
                                  src={spotifyArtist.image_big}
                                  style={{ width: "150px", padding: "10px 0" }}
                                  alt={"Spotify Artist DP"}
                                />
                              </Col>
                            </FormGroup>
                          }
                        </>
                        }

                        { (!isNewArtist && hasDeezerProfile) &&
                          <>
                            <FormGroup className="select-search-wrap" row>
                              <Label for="Deezer Profile" sm={2}>
                                {t("artists.add.form.label.deezer_profile")}
                              </Label>
                              <Col sm={8}>
                                {deezerArtist?
                                <AsyncSelect
                                  defaultOptions={[deezerArtist]}
                                  value={deezerArtist}
                                  onChange={()=> {}}
                                  loadingMessage={() => t("general.asyncDropdown.loading")}
                                  noOptionsMessage={() => t("general.asyncDropdown.noResults")} 
                                  components={{
                                    Option: CustomOption,
                                  }}
                                  styles={{
                                    menu: (styles) => ({ ...styles, zIndex: 10 }),
                                  }}
                                />:
                                <>
                                  <AsyncDropdownDeezer
                                    fetchOptions={options}
                                    labelField="name"
                                    onChange={e => {
                                      let artistId = e.value;
                                      let artistObj = e;
                                      artistObj.name = e.label;
                                      setDeezerArtist(artistObj);
                                      setFieldValue("deezerId", artistId);
                                      if(defaultQuery !== artistObj.name) {
                                        setDefaultQuery(artistObj.name);
                                      }
                                      if(!spotifyArtist && !amazonArtist) {
                                        setNamePreference("deezer");
                                        setFieldValue("name", artistObj.name);
                                        setImageBig(artistObj.image_big);
                                        setImageSmall(artistObj.image_small);
                                      }
                                    }}
                                    defaultQuery={defaultQuery}
                                    isDisabled={deezerArtist? true: false}
                                    placeholder={t("artists.add.form.placeholder.deezer_profile")}
                                  />
                                  <FormText color="muted">
                                    {t("artists.add.form.help_text.deezer_profile")}
                                  </FormText>
                                </>
                                }
                              </Col>
                              <Col sm={2}>
                                {
                                  deezerArtist? 
                                  <Button
                                    className="btn btn-outline-info"
                                    style={{width: '100%'}}
                                    onClick={() => {
                                      setDeezerArtist(null);
                                      setErrorNameMismatch(false);
                                      setErrorDupl(false);
                                      setErrorBlacklisted(false);
                                      setFieldValue("deezerId", "");
                                      if(!spotifyArtist && !amazonArtist) {
                                        setFieldValue("name", "");
                                        setImageBig("");
                                        setImageSmall("");
                                      } else if(namePreference === "deezer") {
                                        if(spotifyArtist) {
                                          setNamePreference("spotify");
                                          setFieldValue("name", spotifyArtist.name);
                                          setImageBig(spotifyArtist.image_big);
                                          setImageSmall(spotifyArtist.image_small);
                                        } else if (amazonArtist) {
                                          setNamePreference("amazon");
                                          setFieldValue("name", amazonArtist.name);
                                          setImageBig(amazonArtist.image_big);
                                          setImageSmall(amazonArtist.image_small);
                                        }
                                      }
                                    }}
                                  >
                                    {t("artists.add.form.button.not_found")}
                                  </Button>:
                                  <Button
                                    className="btn btn-outline-danger"
                                    style={{width: '100%'}}
                                    onClick={() => {
                                      if(enabledProfilesCount>=2) {
                                        setMissingStoreName("Deezer");
                                      }
                                    }}
                                  >
                                    {t("artists.add.form.button.not_found")}
                                  </Button>
                                }
                              </Col>
                            </FormGroup>

                            {
                              deezerArtist &&
                              <FormGroup row>
                                <Label for="deezerId" sm={2}>
                                  {t("artists.add.form.label.deezer_id")}
                                </Label>
                                <Col>
                                  <Field
                                    name="deezerId"
                                    type="text"
                                    disabled={true}
                                    className="form-control"
                                  />
                                </Col>
                              </FormGroup>
                            }
                            {
                              (deezerArtist && deezerArtist.image_big) && 
                              <FormGroup row>
                                <Label sm={2}>{t("artists.add.form.label.deezer_image")}</Label>
                                <Col xs={2}>
                                  <img
                                    src={deezerArtist.image_big}
                                    style={{ width: "150px", padding: "10px 0" }}
                                    alt={"Deezer Artist DP"}
                                  />
                                </Col>
                              </FormGroup>
                            }
                          </>
                        }

                        { (!isNewArtist && hasAmazonProfile) &&
                          <>
                            <FormGroup className="select-search-wrap" row>
                              <Label for="Amazon Music Profile" sm={2}>
                                {t("artists.add.form.label.amazon_music_profile")}
                              </Label>
                              <Col>
                                {amazonArtist?
                                <AsyncSelect
                                  defaultOptions={[amazonArtist]}
                                  value={amazonArtist}
                                  onChange={()=> {}}
                                  loadingMessage={() => t("general.asyncDropdown.loading")}
                                  noOptionsMessage={() => t("general.asyncDropdown.noResults")} 
                                  components={{
                                    Option: CustomOption,
                                  }}
                                  styles={{
                                    menu: (styles) => ({ ...styles, zIndex: 10 }),
                                  }}
                                />:
                                <>
                                  <AsyncDropdownAmazon
                                    fetchOptions={options}
                                    labelField="name"
                                    onChange={e => {
                                      let artistId = e.value;
                                      let artistObj = e;
                                      artistObj.name = e.label;
                                      setAmazonArtist(artistObj);
                                      setFieldValue("amazonId", artistId);
                                      if(defaultQuery !== artistObj.name) {
                                        setDefaultQuery(artistObj.name);
                                      }
                                      if(!spotifyArtist && !deezerArtist) {
                                        setNamePreference("amazon");
                                        setFieldValue("name", artistObj.name);
                                        setImageBig(artistObj.image_big);
                                        setImageSmall(artistObj.image_small);
                                      }
                                    }}
                                    defaultQuery={defaultQuery}
                                    isDisabled={amazonArtist? true: false}
                                    placeholder={t("artists.add.form.placeholder.amazon_music_profile")}
                                  />
                                  <FormText color="muted">
                                    {t("artists.add.form.help_text.amazon_music_profile")}
                                  </FormText>
                                </>
                                }
                              </Col>
                              <Col sm={2}>
                                {
                                  amazonArtist? 
                                  <Button
                                    className="btn btn-outline-info"
                                    style={{width: '100%'}}
                                    onClick={() => {
                                      setAmazonArtist(null);
                                      setErrorNameMismatch(false);
                                      setErrorDupl(false);
                                      setErrorBlacklisted(false);
                                      setFieldValue("amazonId", "");
                                      if(!spotifyArtist && !deezerArtist) {
                                        setFieldValue("name", "");
                                        setImageBig("");
                                        setImageSmall("");
                                      } else if(namePreference === "amazon") {
                                        if(spotifyArtist) {
                                          setNamePreference("spotify");
                                          setFieldValue("name", spotifyArtist.name);
                                          setImageBig(spotifyArtist.image_big);
                                          setImageSmall(spotifyArtist.image_small);
                                        } else if (deezerArtist) {
                                          setNamePreference("deezer");
                                          setFieldValue("name", deezerArtist.name);
                                          setImageBig(deezerArtist.image_big);
                                          setImageSmall(deezerArtist.image_small);
                                        }
                                      }
                                    }}
                                  >
                                    {t("artists.add.form.button.not_found")}
                                  </Button>:
                                  <Button
                                    className="btn btn-outline-danger"
                                    style={{width: '100%'}}
                                    onClick={() => {
                                      if(enabledProfilesCount>=2) {
                                        setMissingStoreName("Amazon Music");
                                      }
                                    }}
                                  >
                                    {t("artists.add.form.button.not_found")}
                                  </Button>
                                }
                              </Col>
                            </FormGroup>

                            {
                              amazonArtist &&
                              <FormGroup row>
                                <Label for="amazonId" sm={2}>
                                  {t("artists.add.form.label.amazon_music_id")}
                                </Label>
                                <Col>
                                  <Field
                                    name="amazonId"
                                    type="text"
                                    disabled={true}
                                    className="form-control"
                                  />
                                </Col>
                              </FormGroup>
                            }
                            {
                              (amazonArtist && amazonArtist.image_big) && 
                              <FormGroup row>
                                <Label sm={2}>{t("artists.add.form.label.amazon_music_image")}</Label>
                                <Col xs={2}>
                                  <img
                                    src={amazonArtist.image_big}
                                    style={{ width: "150px", padding: "10px 0" }}
                                    alt={"Amazon Music Artist DP"}
                                  />
                                </Col>
                              </FormGroup>
                            }
                          </>
                        }

                        { (!isNewArtist && hasAppleProfile) &&
                          <FormGroup row>
                            <Label for="appleId" sm={2}>
                              {t("artists.add.form.label.apple_id")}
                            </Label>
                            <Col sm={8}>
                              <Field
                                name="appleId"
                                type="text"
                                placeholder={t("artists.add.form.placeholder.apple_id")}
                                className={
                                  "form-control" +
                                  (errors.appleId && touched.appleId
                                    ? " is-invalid"
                                    : "")
                                }
                              />
                              <FormText color="muted">
                                {t("artists.add.form.help_text.apple_id")}
                              </FormText>
                              <ErrorMessage
                                name="appleId"
                                component="div"
                                className="invalid-feedback"
                              />
                            </Col>
                            <Col sm={2}>
                                <Button
                                  className="btn btn-outline-danger"
                                  style={{width: '100%'}}
                                  onClick={() => {
                                    setMissingStoreName("Apple Music");
                                  }}
                                >
                                  {t("artists.add.form.button.not_found")}
                                </Button>
                            </Col>
                          </FormGroup>
                        }

                        <FormGroup row>
                          <Label for="name" sm={2}>
                            {t("artists.add.form.label.name")}
                          </Label>
                          <Col>
                            <Field
                              name="name"
                              type="text"
                              disabled={!isNewArtist}
                              className={
                                "form-control" +
                                ((errorNameMismatch || errors.name)  && touched.name ? " is-invalid" : "")
                              }
                            />
                            <ErrorMessage
                              name="name"
                              component="div"
                              className="invalid-feedback"
                            />
                            {
                              errorDupl && <p style={{fontSize: "12px",color: "red",margin: "2px 0 0 0"}}>{t("artists.add.form.validation.name_duplicate")}</p>
                            }
                            {
                              errorNameMismatch && <p style={{fontSize: "12px",color: "red",margin: "2px 0 0 0"}}>{t("artists.add.form.validation.name_mismatch")}</p>
                            }
                          </Col>
                        </FormGroup>

                        
                        { selectedArtistsCount>1 &&
                          <FormGroup row>
                            <Label for="Name Preference" sm={2}>
                              {t("artists.add.form.label.name_preference")}
                            </Label>
                            <Col>
                              <div style={{ display: "flex", justifyContent: "normal" }} >
                                {
                                  spotifyArtist &&
                                  <FormGroup check className="mr-4">
                                    <Label check>
                                      <Input
                                        type="radio"
                                        name="prefer-spotify"
                                        value="spotify"
                                        checked={namePreference === "spotify"}
                                        onChange={() => {
                                          setNamePreference("spotify")
                                          setFieldValue("name", spotifyArtist.name);
                                        }}
                                      />{" "}
                                      <span className="mt-1">{t("artists.add.form.label.spotify")}</span>
                                    </Label>
                                  </FormGroup>
                                }

                                {
                                  deezerArtist &&
                                  <FormGroup check className="mr-4">
                                    <Label check>
                                      <Input
                                        type="radio"
                                        name="prefer-deezer"
                                        value="spotify"
                                        checked={namePreference === "deezer"}
                                        onChange={() => {
                                          setNamePreference("deezer")
                                          setFieldValue("name", deezerArtist.name);
                                        }}
                                      />{" "}
                                      <span className="mt-1">{t("artists.add.form.label.deezer")}</span>
                                    </Label>
                                  </FormGroup>
                                }

                                {
                                  amazonArtist &&
                                  <FormGroup check className="mr-4">
                                    <Label check>
                                      <Input
                                        type="radio"
                                        name="prefer-amazon"
                                        value="amazon"
                                        checked={namePreference === "amazon"}
                                        onChange={() => {
                                          setNamePreference("amazon")
                                          setFieldValue("name", amazonArtist.name);
                                        }}
                                      />{" "}
                                      <span className="mt-1">{t("artists.add.form.label.amazon_music")}</span>
                                    </Label>
                                  </FormGroup>

                                }

                              </div>
                            </Col>
                          </FormGroup>
                        }

                        <FormGroup row>
                          <Label for="email" sm={2}>
                            {t("artists.add.form.label.email")}
                          </Label>
                          <Col>
                            <Field
                              name="email"
                              type="text"
                              className={
                                "form-control" +
                                (errors.email && touched.email
                                  ? " is-invalid"
                                  : "")
                              }
                            />
                            <ErrorMessage
                              name="email"
                              component="div"
                              className="invalid-feedback"
                            />
                          </Col>
                        </FormGroup>

                        {currentUser.is_premium_user &&
                          <FormGroup className="select-search-wrap" row>
                            <Label for="subUser" sm={2}>
                              {t("artists.add.form.label.subuser")}
                            </Label>
                            <Col>
                              {
                                subUser ?
                                <div className="releaseFileRow">
                                  <p className="releaseFileRowName"> {subUser}</p>
                                  <Button className="btn btn-outline-info"
                                    onClick={() => {
                                      setSubUser('');
                                      setSubUserId(null);
                                      setSubUserEndpoint('');
                                    }
                                    }>
                                    {t("artists.add.form.button.reset")}
                                  </Button>
                                </div>
                                :
                                <AsyncDropdownNormal
                                  fetchOptions={options}
                                  endpoint={`sub-users`}
                                  subUserEndpoint={subUserEndpoint}
                                  parentUserOnly={parentUserOnly}
                                  labelField="username"
                                  onChange={e => {
                                    let subUserId = e.value;
                                    let username = e.label;
                                    if(subUserId !== '') {
                                      setSubUser(username);
                                      setSubUserId(subUserId);
                                      setSubUserEndpoint(`sub-user/${subUserId}/`);
                                      setParentUserOnly('');
                                    } else {
                                      setSubUser('');
                                      setSubUserId(null);
                                      setSubUserEndpoint('');
                                      setParentUserOnly('&parent_user_only=true');
                                    }
                                  }}
                                  placeholder={t("artists.add.form.placeholder.subuser")}
                                />
                              }
                            <FormText color="muted">
                              {t("artists.add.form.help_text.subuser")}
                            </FormText>
                            </Col>
                          </FormGroup>
                        }

                        <FormGroup row>
                          <Col>
                            <Button color="success" disabled={errorDupl || errorNameMismatch || errorProfileUnmatched || errorBlacklisted}>{t("artists.add.form.button.submit")}</Button>
                          </Col>
                        </FormGroup>

                        <Modal isOpen={profileMissingModalVisible} centered={true}>
                          <ModalHeader>
                            {t("artists.add.modal.title.profile_not_present_declaration")}
                          </ModalHeader>
                          <ModalBody>
                            <Trans i18nKey="artists.add.modal.body.profile_not_present_declaration" values={{ store: missingStoreName }}>
                              I confirm that this Artist's profile is not present on <strong>{{missingStoreName}}</strong> and that the profile of this Artist be created freshly on <strong>{{missingStoreName}}</strong>.
                            </Trans>
                          </ModalBody>
                          <ModalFooter>
                            <Button
                              color="secondary"
                              onClick={() => {
                                setProfileMissingModalVisible(false);
                                setMissingStoreName("");
                              }}
                            >
                              {t("artists.add.modal.button.cancel")}
                            </Button>
                            <Button
                              color="success"
                              onClick={() => {
                                if(missingStoreName === "Spotify") {
                                  setHasSpotifyProfile(false);
                                  setSpotifyArtist(null);
                                  setFieldValue("spotifyId", "");
                                }
                                if(missingStoreName === "Deezer") {
                                  setHasDeezerProfile(false);
                                  setDeezerArtist(null);
                                  setFieldValue("deezerId", "");
                                }
                                if(missingStoreName === "Amazon Music") {
                                  setHasAmazonProfile(false);
                                  setAmazonArtist(null);
                                  setFieldValue("amazonId", "");
                                }
                                if(missingStoreName === "Apple Music") {
                                  setHasAppleProfile(false);
                                  setFieldValue("appleId", "");
                                }
                                setProfileMissingModalVisible(false);
                                setMissingStoreName("");
                              }}
                            >
                              {t("artists.add.modal.button.confirm")}
                            </Button>
                          </ModalFooter>
                        </Modal>
                        
                        <Modal isOpen={newArtistModalVisible} centered={true}>
                          <ModalHeader>
                            {t("artists.add.modal.title.new_artist_declaration")}
                          </ModalHeader>
                          <ModalBody>
                            {t("artists.add.modal.body.new_artist_declaration")}<br/><br/>
                            <ul>
                              <li><b>{t("artists.add.modal.body.new_artist_declaration_amazon_music")}</b></li>
                              <li><b>{t("artists.add.modal.body.new_artist_declaration_apple_music")}</b></li>
                              <li><b>{t("artists.add.modal.body.new_artist_declaration_deezer")}</b></li>
                              <li><b>{t("artists.add.modal.body.new_artist_declaration_spotify")}</b></li>
                            </ul>
                          </ModalBody>
                          <ModalFooter>
                            <Button
                              color="secondary"
                              onClick={() => setNewArtistModalVisible(false)}
                            >
                              {t("artists.add.modal.button.cancel")}
                            </Button>
                            <Button
                              color="success"
                              onClick={() => {
                                setFieldValue("name", "")
                                setFieldValue("amazonId", "")
                                setFieldValue("deezerId", "")
                                setFieldValue("spotifyId", "")
                                setFieldValue("appleId", "")
                                setImageBig("");
                                setImageSmall("");
                                setAmazonArtist(null)
                                setHasAmazonProfile(true)
                                setDeezerArtist(null)
                                setHasDeezerProfile(true)
                                setSpotifyArtist(null)
                                setHasSpotifyProfile(true)
                                setHasAppleProfile(true)
                                setErrorProfileUnmatched(false)
                                setNewArtistModalVisible(false)
                                setIsNewArtist(true)
                              }}
                            >
                              {t("artists.add.modal.button.confirm")}
                            </Button>
                          </ModalFooter>
                        </Modal>
                    </Form>
                  );
                }}
              />
              {success && <Alert color="success">{t("artists.add.message.success")}</Alert>}
              {redirect ? <Redirect to="/artists" /> : null}
              {error && (
                <Alert color="danger">
                  {t("artists.add.message.error")}
                </Alert>
              )}
              {
                errorBlacklisted && (
                  <Alert color="danger">
                    {t("artists.add.message.errorBlacklisted")}
                  </Alert>
                )
              }
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ArtistsAdd;